import { AlertTriangle, CreditCard } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  ErrorState,
  LoadingState,
  RestrictedState,
} from "@/components/feedback";
import { useAppConfig, useBilling, useBillingPortal, useUpdateSubscription } from "@/hooks/use-billing";
import { notifyError } from "@/lib/errors";
import { cn } from "@/lib/utils";

/** A tiny obsidian-styled on/off switch — there's no shared `Switch` primitive
 * yet (only `Checkbox` in `ui/primitives.tsx`), and repurposing a checkbox for
 * a subscription toggle reads oddly, so this stays a small local control
 * rather than growing a new shared primitive outside this task's ownership. */
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "border-primary bg-primary/80" : "border-border bg-muted",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow transition-transform",
          checked ? "translate-x-[18px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

function AddonRow({
  title,
  price,
  description,
  available,
  checked,
  busy,
  onToggle,
}: {
  title: string;
  price: string;
  description: string;
  available: boolean;
  checked: boolean;
  busy: boolean;
  onToggle: (v: boolean) => void;
}) {
  if (!available) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-3 opacity-60">
        <p className="text-sm text-foreground">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Not available on this deployment.</p>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm text-foreground">
          {title}
          <span className="font-mono text-[11px] text-primary">{price}</span>
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onToggle} disabled={busy} label={`${title} — ${checked ? "on" : "off"}`} />
    </div>
  );
}

/**
 * The billing content — reused by Settings → Billing (embedded in a tab) AND
 * the top-right `<UpgradeButton>` (embedded in its own dialog). Renders the
 * two independent add-ons (unlimited storage, frontier models), lets the user
 * toggle either (POST /billing/update handles both "start a subscription" and
 * "modify an existing one" — a fresh `checkout_url` redirects to Stripe;
 * otherwise the toggle applies immediately), a "Manage in portal" escape
 * hatch, and cancel (= both off). Explicit failure states throughout: billing
 * disabled/unavailable → RestrictedState, past-due payment → an inline
 * banner with a portal CTA, request failures → ErrorState with retry.
 */
export function BillingPanel() {
  const config = useAppConfig();
  const billing = useBilling();
  const update = useUpdateSubscription();
  const portal = useBillingPortal();

  if (config.isLoading || billing.isLoading) {
    return <LoadingState variant="skeleton" rows={4} label="Loading billing…" />;
  }
  if (config.isError) {
    return <ErrorState error={config.error} onRetry={() => void config.refetch()} label="billing" />;
  }
  if (billing.isError) {
    return <ErrorState error={billing.error} onRetry={() => void billing.refetch()} label="billing" />;
  }

  const cfg = config.data;
  const status = billing.data;
  if (!cfg || !status) {
    return <ErrorState error={new Error("missing billing data")} onRetry={() => void billing.refetch()} label="billing" />;
  }

  if (!cfg.features.billing || !status.enabled) {
    return (
      <RestrictedState
        reason="billing"
        title="Billing isn't available"
        description="This deployment doesn't offer self-serve upgrades — reach out if you need more storage or frontier models."
      />
    );
  }

  const pastDue = status.status === "past_due";
  const hasSub = status.status !== "none";
  const anyActive = status.storage_unlimited || status.models_premium;

  const openPortal = () => {
    portal.mutate(undefined, {
      onSuccess: (res) => {
        window.location.href = res.url;
      },
      onError: (e: unknown) => notifyError(e),
    });
  };

  const applyChange = (next: { storage: boolean; models: boolean }) => {
    update.mutate(next, {
      onSuccess: (res) => {
        if ("checkout_url" in res) {
          window.location.href = res.checkout_url;
          return;
        }
      },
      onError: (e: unknown) => notifyError(e),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {pastDue && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-[13px] text-destructive-foreground"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1">Your last payment failed. Update your billing info to keep your add-ons.</span>
          <Button type="button" size="sm" variant="outline" disabled={portal.isPending} onClick={openPortal}>
            Update payment
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <AddonRow
          title="Unlimited storage"
          price={`$${cfg.billing.storage.unlimited_price_usd}/mo`}
          description={`Free tier caps at ${Math.round(cfg.billing.storage.free_mb / 1024) || 1} GB.`}
          available={cfg.billing.storage.available}
          checked={status.storage_unlimited}
          busy={update.isPending}
          onToggle={(v) => applyChange({ storage: v, models: status.models_premium })}
        />
        <AddonRow
          title="Frontier models"
          price={`+$${cfg.billing.models.price_usd}/mo`}
          description="Claude, Gemini, and GPT-class models instead of free-tier models."
          available={cfg.billing.models.available}
          checked={status.models_premium}
          busy={update.isPending}
          onToggle={(v) => applyChange({ storage: status.storage_unlimited, models: v })}
        />
      </div>

      {status.current_period_end && (
        <p className="text-xs text-muted-foreground">
          {status.cancel_at_period_end ? "Add-ons end" : "Renews"} on{" "}
          {new Date(status.current_period_end).toLocaleDateString()}.
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        {hasSub && (
          <Button type="button" size="sm" variant="outline" disabled={portal.isPending} onClick={openPortal}>
            <CreditCard className="h-3.5 w-3.5" /> Manage in portal
          </Button>
        )}
        {anyActive && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            disabled={update.isPending}
            onClick={() => applyChange({ storage: false, models: false })}
          >
            Cancel add-ons
          </Button>
        )}
      </div>
    </div>
  );
}
